import { isEqual, omit } from 'lodash';
import { IPlainObject } from '../types/interfaces';

/**
 * @param objA // Any object
 * @param objB // Any generic object
 * @param excludeKey // Keys, that we want to exclude from isEqual check
 * @returns true/false value, of is isEqual check between obj1 and obj2.
 */

export const isEqualWithoutSomeKeys = <T extends object>(
  objA: T[],
  objB: T[],
  excludeKey: keyof T | (keyof T)[]
) => {
  return isEqual(
    objA.map((a) => omit(a, excludeKey)),
    objB.map((b) => omit(b, excludeKey))
  );
};

/**
 * pull all values from the object, include nested values, and return them in array
 * @param object any object
 * @returns an array of all values, include nested values, of an object
 */

export const getAllObjectValues = <T = string>(object: IPlainObject): T[] => {
  let routeValues = [];

  if (typeof object === 'object') {
    routeValues = Object.values(object);
  }

  routeValues.forEach((routeValue) => {
    if (typeof routeValue === 'object') {
      routeValues = [
        ...routeValues.filter((route) => route !== routeValue),
        ...getAllObjectValues(routeValue)
      ];
    }
  });

  return routeValues;
};

/**
 * @param obj // any generic object
 * @param keys // array of string (keys), that you want pick from an object
 * @returns an object, that contain only keys and values, that were passed as keys
 */

export const pickFromObject = <T extends object, K extends keyof T>(obj: T, keys: K[]) =>
  keys.reduce(
    (carry, key) => ({
      ...carry,
      [key]: obj[key]
    }),
    {} as Pick<T, K>
  );

// Get any object with any depth and return the first path (until a dead end) of this object in string.

export const getFirstObjectPath = (obj: object, path = ''): string => {
  const firstKey = Object.keys(obj)[0];
  const nestedObj = obj[firstKey];

  let newPath = firstKey;

  if (path !== '') {
    newPath = Array.isArray(obj) ? `${path}[${firstKey}]` : `${path}.${firstKey}`;
  }

  if (typeof nestedObj === 'object') {
    return getFirstObjectPath(nestedObj, newPath);
  }

  return newPath;
};
